import { useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Button } from '@attendance/ui/components/Button';
import { cn } from '@attendance/ui/utils';
import { useIsCompact, ICON } from '@attendance/ui';
import { Toaster } from 'sonner';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isWideRoute } from './navItems.js'; import { showsFooter } from './footerRoutes.js';
import { Sidebar } from './Sidebar.js';
import { MobileBottomNav, PHONE_TOAST, PHONE_TOAST_BOTTOM_PX } from './MobileBottomNav.js';
import { useShellSurface } from './shellSurface.js';
import { CommandPalette } from './CommandPalette.js';
import { AppFooter } from './AppFooter.js';
import { SkipToContent } from './SkipToContent.js';
import { greetingBucketIn, greetingName } from './shellGreeting.js';
import { useAuthStore } from '../../stores/authStore.js';
import { useCompanyTimeZone } from '../../hooks/useFormatDate.js';
import { NotificationBell } from './NotificationBell.js';
import { PageScrolledProvider, useScrolledPastSentinel } from './pageScroll.js';
import { HeaderSearch } from './HeaderSearch.js';
import { ViewAsBanner } from './ViewAsBanner.js';

/** English defaults for the header greeting; the visible string is resolved
 *  through `t()` at render time, same idiom as every other copy default. */
const GREETING_DEFAULT = {
  morning: 'Good morning, {{name}}',
  afternoon: 'Good afternoon, {{name}}',
  evening: 'Good evening, {{name}}',
} as const;

export function AppShell(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { t } = useTranslation();
  // Phone shape = the worker's workspace only; reasoning in `shellSurface.ts`.
  const { phoneShaped } = useShellSurface();
  /* Width, not route: `phoneShaped` is true for the personal workspace at
     every width, and the footer is a question about how much room there is. */
  const compact = useIsCompact();
  const location = useLocation();
  /* THE HOUR IS THE COMPANY'S, NOT THE BROWSER'S — docs/time-and-dates.md. A
     supervisor opening the console from another country is greeted by the
     site's afternoon, because the site's day is what this product is about. */
  const timeZone = useCompanyTimeZone();
  const greetBucket = greetingBucketIn(timeZone);
  const greetName = greetingName(user?.displayName, user?.email);

  /* ------------------------------------------------------------------------
   * THE STICKY TOP BAR, and how it knows it has been scrolled under.
   *
   * The bar's scrolled state is a boolean, but the interesting part is WHERE the
   * threshold is measured. `window.scrollY` is useless here: the window never
   * scrolls in this shell — `h-screen overflow-hidden` on the root means the only
   * thing that scrolls is the content column, so the 12px the design asks for is
   * 12px of THAT element's scroll offset.
   *
   * Two ways to read it. The handoff's `attachStickyBar` adds a passive `scroll`
   * listener and compares `scrollTop > 12` on every event; this uses an
   * IntersectionObserver against a 12px sentinel pinned to the top of the
   * scrolled content instead. Same threshold, but the browser evaluates it off
   * the main thread and calls back only when the answer CHANGES — a scroll
   * listener fires for all 900px of a long page to re-derive a boolean that
   * flipped in the first 12 of them.
   *
   * The sentinel is `absolute`, so it takes no space in the flex column and
   * cannot push the bar down; it is positioned against the scroll container's
   * padding box, which scrolls with the content, so it leaves the scrollport
   * exactly when `scrollTop` passes its own 12px height. It sits UNDER the bar
   * (the bar is `z-30`) and is `aria-hidden` with no content, so it is invisible
   * to the eye, to the pointer and to assistive tech.
   *
   * The hook is gated on a constant `true` rather than `[]`: the shell is
   * always mounted, but the dependency stays explicit rather than becoming
   * `[]`, which would read as "run once" and is not what this wants.
   *
   * THE MEASUREMENT NOW LIVES IN `pageScroll.tsx` AND THE ANSWER IS PUBLISHED.
   * It used to be this component's private state, which is why the page-level
   * sticky toolbar §2 of the rules file asks for existed on one screen: every
   * other screen would have had to add its own listener to know the same fact.
   * The observer is still one observer against one sentinel — `PageShell` now
   * subscribes to its result through `PageScrolledProvider` below, so the shell
   * bar and the page bar flip on the same pixel and neither pays twice.
   * --------------------------------------------------------------------- */
  const scrollRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);

  // Shared with the mobile "Alerts" tab — one query, one number. See
  // hooks/useUnreadNotifications.ts.

  /* A WORKER'S PHONE HAS NO DRAWER. Every personal destination is a tab or a
     row on the profile page (`ProfileShortcuts`), so search, theme and sign-out
     leave this bar there too — what stays is the greeting and the bell. */
  const workerPhone = phoneShaped && compact;
  /* The profile page is the phone's settings screen: its own header card names
     the person, and the bell is one tab away — so it draws no bar at all. */
  const bareBar = workerPhone && location.pathname === '/profile';

  const scrolled = useScrolledPastSentinel(scrollRef, topSentinelRef, true);

  return (
    /* THE CAPSULE'S BAND, RESERVED ON THE SHELL — outside the scrolling column,
       so the column is 80px shorter and its scrollport stops above the capsule.
       Padding on the scroller cannot do this: it adds space after the content,
       it does not shorten the viewport. 80px is measured (16 offset + 56 capsule
       + 8 of air); `h-dvh` so a mobile toolbar never covers it. Why `phoneShaped`: shellSurface.ts. */
    /* The view-as band (ViewAsBanner.tsx) sits ABOVE the shell row, so it never
       moves the sticky bar's 56px datum; the row takes the height left over. */
    <div className="flex h-dvh flex-col overflow-hidden bg-background">
    <ViewAsBanner />
    <div className={cn('flex min-h-0 flex-1 overflow-hidden', phoneShaped && 'max-md:pb-20')}>
      <SkipToContent />

      {/* The rail: a 252px ink column in the flex row at md+, and not drawn on a
          worker's phone, where the bottom bar and the profile page's rows stand
          in for it.

          It is deliberately OUTSIDE the scroll container below. The rail scrolls
          its own overflow and the page scrolls its own, so a long back-office
          list of sections does not have to be scrolled past to reach the page —
          and the ink column keeps reaching the bottom of the viewport whatever
          the page height is. (It used to say "solid brand-coloured": the rail
          stopped being a slab of `--primary` when it took its own three tokens;
          see the comment block at the top of Sidebar.tsx.) */}
      <Sidebar />

      {/* THE CONTENT COLUMN — and the app's one scroll container.
          =====================================================================
          THIS IS THE STRUCTURAL CHANGE, so it is worth saying what moved and
          why the old shape could not carry a sticky bar.

          It used to be a plain flex column holding a `shrink-0` header and a
          `<main>` that owned `overflow-auto`. The bar was therefore OUTSIDE the
          thing that scrolls: `position: sticky` on it had nothing to stick to
          (its scrollport was the window, which never scrolls here), and every
          page-level sticky element measured its offset from the top of `main` —
          i.e. from a line that already sat 56px down the screen. That is why the
          app is full of `sticky top-0`, and why the rules file says the opposite:
          a page-level sticky sits at `top: 56px` (`top-header`), never `top: 0`.

          Now the COLUMN scrolls and both the bar and the page live inside it:

            column   overflow-y-auto, position: relative  ← the scrollport
              ├ sentinel   absolute, 12px, the scrolled-state tripwire
              ├ header     sticky top-0, 56px             ← never scrolls away
              └ main       flex-1                         ← the page

          Three consequences, all of them the point:
            · the bar sticks, because the element it must stick to is now its own
              ancestor rather than its sibling;
            · a screen's own sticky bar at `top-header` lands exactly under it,
              measured from the same scrollport, so the two cannot disagree;
            · the scrollbar is still at the window edge and the page background
              still fills past the reading cap, because the element that scrolls
              is still full-width.

          The corollary is that a page-level `sticky top-0` now hides UNDER the
          bar instead of parking below it, which is the whole reason the rules
          file bans `top: 0` outright. Four screens still declare one and belong
          to the screen wave, not to this file: EmployeeFormPage's field rail,
          LeaveManagePage's bulk bar, ReportsLayout's `top-6` report rail and
          BrandingSettingsPage's `top-4` preview card. (Sticky TABLE headers are
          unaffected — the calendar and schedule grids scroll inside their own
          `overflow-auto` box, so their scrollport was never this one.)

          `main` keeps `flex-1` and so keeps a resolved height — which is what
          `h-full` on the wide-route wrapper below is measured against — and it
          keeps being the skip-link target, one landmark below the banner. What
          it no longer has is a scrollbar of its own. */}
      <div
        ref={scrollRef}
        className="scrollbar-gutter-stable relative flex min-w-0 flex-1 flex-col overflow-y-auto bg-background"
      >
        {/* The 12px tripwire. Its HEIGHT is the threshold — `h-3` is 12px, and
            the observer above flips the moment this box leaves the scrollport,
            which is `scrollTop > 12` expressed as geometry instead of as a
            number compared on every scroll event. */}
        <div
          ref={topSentinelRef}
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-3"
        />

        {!bareBar && (<header
          className={cn(
            // `min-h-header`, not `h-header`: a fixed height on a bar carrying a
            // display name of unknown length clips it. It must still MEASURE
            // 56px, because `top-header` offsets every page-level sticky by that
            // token — so nothing in this bar is allowed to wrap, and nothing in
            // it is taller than the 44px hamburger.
            //
            // `z-[35]`, NOT `z-30`. This bar hosts the notification bell's own
            // popover (NotificationBell.tsx, `z-50`), and a positioned element
            // with its own z-index opens a new STACKING CONTEXT: the popover's
            // `z-50` only ever ranks against its OWN siblings inside this
            // `header`, never against anything outside it. Every page-level
            // sticky toolbar (PageShell.tsx) is ALSO `z-30`, sitting in a
            // sibling stacking context of its own (`main` creates none), so the
            // two tied — and on a tie the later element in paint order wins,
            // which is the toolbar. Opening the bell while scrolled past a
            // page's toolbar therefore painted the toolbar OVER the open
            // popover. One tier above the toolbars, and — the reason it stops
            // at 35 rather than reaching for the bell's own 50 — still below the
            // overlays (`z-40`/`z-50`), so a modal's scrim dims this bar too.
            'sticky top-0 z-[35] flex min-h-header shrink-0 items-center justify-between gap-inline',
            // The page gutter, shared with the content below it, so the
            // signed-in name sits on the same line as the page title rather than
            // at an inset of the bar's own invention.
            'px-gutter-sm md:px-gutter',
            // A worker's phone: no bar at rest — the greeting sits on the page
            // (on Home, on the map) as drawn; glass once content runs under it.
            workerPhone
              ? scrolled ? 'bg-background/85 shadow-sticky backdrop-blur-md' : 'bg-transparent'
              : 'border-b border-border',
            /* THE SCROLLED STATE — two properties, and NEITHER of them is size.
               The bar must not change height when content passes under it: it is
               the datum `top-header` is measured from, so a bar that grows by a
               pixel takes every sticky toolbar in the app with it.

               So the state is a shadow plus a denser background, exactly as the
               handoff has it (its gradient's solid stop runs 62% → 88%). Here
               the gradient's far stop goes from a translucent `surface` to a
               fully opaque one: at rest nothing is behind the bar, so the
               translucency is invisible; once the page is running underneath,
               opaque is what stops content ghosting through the last few pixels
               above the hairline. `duration-elevate` because an elevation
               change is the one thing that token is for — at `duration-fast` a
               28px shadow appears rather than grows. */
            'transition-shadow duration-elevate ease-standard',
            !workerPhone && 'bg-gradient-to-b',
            !workerPhone && (scrolled ? 'from-surface to-surface shadow-sticky' : 'from-surface to-surface/90')
          )}
        >
          <div className="flex min-w-0 items-center gap-element">
            {/* THE GREETING IS THE IDENTITY, and it is the one piece of TEXT
                in this bar.

                It said the name alone while the worker's home screen said
                "Good afternoon, <name>" in an h1 forty pixels below it — the
                same fact twice, and on a phone the second one pushed the punch
                control most of the way down the first screenful. The home
                screen says neither now; this says both, in the words a person
                would use (owner: "top left keep short greeting with first name
                only"). `shellGreeting.ts` carries why it is the first WORD.

                `truncate` keeps a long name from wrapping the bar past the 56px
                every page sticky is offset by. */}
            <span className="truncate text-copy font-semibold text-foreground">
              {greetName === null
                ? t('app.signedIn', 'Signed in')
                : t(`appShell.greeting.${greetBucket}`, GREETING_DEFAULT[greetBucket], {
                    name: greetName,
                  })}
            </span>
          </div>
          {/* The search is the first of the bar's icons, not an island in the
              middle (owner, 2026-10-13); opened, it grows leftwards from there. */}
          <div className="flex min-w-0 flex-1 items-center justify-end gap-tight">
            {!workerPhone && <HeaderSearch />}
            {/* Theme and language moved to Profile → Preferences: set once, not
                every day (owner, 2026-10-14). */}
            {/* A worker's phone: the bell sits in a white disc, as drawn — it
                floats over the map on Home, where a bare glyph is lost. */}
            <span className={workerPhone ? 'rounded-full bg-surface shadow-resting' : 'contents'}>
              <NotificationBell enabled />
            </span>
            {!workerPhone && (
              <Button variant="ghost" size="sm" onClick={() => void logout()}>
                <LogOut size={ICON.control} className="me-1" />
                <span className="hidden sm:inline">{t('appShell.signOut', 'Sign out')}</span>
              </Button>
            )}
          </div>
        </header>)}
        <main id="main" tabIndex={-1} className="flex min-w-0 flex-1 flex-col focus:outline-none">
          {/* The GUTTER and the reading-width cap sit on an inner wrapper, not on
              `main` itself, so `main` stays full-width: the background has to
              keep filling past the cap on a wide monitor, and the column that
              scrolls (one level up) has to keep its scrollbar at the window edge.
              Extra bottom padding on mobile so content clears the bottom nav.

              `p-gutter-sm md:p-gutter` REPLACES `p-4 md:p-6`. The gutter used to
              be a number that lived only here, which meant a sticky bar working
              out its own inset, a full-bleed table trying to escape the padding
              and a modal aligning to the column each had to re-guess it. It is
              now `--gutter` / `--gutter-sm` (34px desktop / 16px phone) — the
              desktop value is wider than the 24px it replaces on purpose: the
              room came out of card padding, because a card reads as a card
              because of the space AROUND it. `md:p-gutter` still wins over
              `pb-24` at md+, exactly as `md:p-6` did, because Tailwind emits
              every variant after the unprefixed utilities.

              A CONTROL ROOM OPTS OUT. The reading cap is right for a form and a
              table and wrong for a wall display: a map capped at the text
              measure, with a metre of background either side of it, is not a
              control room. Those routes take the full width and manage their
              own padding — the exception is declared here, in the one place
              that owns the cap, rather than each page fighting the wrapper with
              negative margins. */}
          <div
            className={
              isWideRoute(location.pathname)
                ? showsFooter(location.pathname)
                  ? 'flex min-h-0 w-full flex-1 flex-col' // the Overview: leaves the footer its line
                  : 'h-full w-full'
                : 'mx-auto flex w-full max-w-content flex-1 flex-col p-gutter-sm md:p-gutter'
            }
          >
            {/* The scrolled boolean measured above, handed to the routed page.
                A page-level sticky toolbar (`PageShell`) reads it from here
                instead of attaching a scroll listener of its own — see the
                note at the top of pageScroll.tsx. */}
            <PageScrolledProvider value={scrolled}>
              <Outlet />
            </PageScrolledProvider>
          </div>
          {/* Every ordinary screen; never a wide route — see `AppFooter`.

              A SIBLING OF THE READING COLUMN, NOT ITS LAST CHILD, and that is
              the whole of the footer bug. It used to sit INSIDE the padded
              wrapper above, so the wrapper's own bottom padding was drawn
              UNDER it — `pb-24` on a phone, `md:pb-gutter` (34px) on a desktop.
              `mt-auto` pushes an item to the end of the CONTENT box, and the
              content box stops where the padding starts, so the footer parked a
              gutter short of the floor however tall the column was. The
              previous fix went after the height chain (`main` a flex column,
              the wrapper `flex-1`) and cut the padding from 96px to 34 — which
              is why it came back smaller instead of fixed.
              Out here the wrapper's bottom padding becomes the AIR ABOVE the
              footer, where it reads as separation, and the footer owns the
              space below itself: the rail's build stamp and this line now sit
              on the same floor. `AppFooter` carries the clearance for the
              phone's bottom navigation, since it is the lowest thing on the
              page. */}
          {/* AND NOT ON A PHONE. The reference has this line on none of its
              ten phone drawings. On a phone it closes the profile page's
              rows (`ProfileShortcuts`), with the build stamp. On a 390px screen the credit was the last band
              above the bottom navigation of a screen whose whole job is one
              button; on a desktop it costs nothing and stays. Not rendered
              rather than hidden: a hidden footer is still a link a screen
              reader walks into. */}
          {showsFooter(location.pathname) && !compact && <AppFooter />}
        </main>
      </div>

      {/* Mobile bottom navigation — five tabs, and no drawer behind it. */}
      {phoneShaped && <MobileBottomNav />}
      {/* ⌘K / Ctrl+K command palette — self-managed open state. */}
      <CommandPalette />

      {/* A worker's phone: the mock's toast — ink, at the bottom, just above the
          floating action; an error keeps its own hue so it never reads as done. */}
      <Toaster
        position={workerPhone ? 'bottom-center' : 'top-right'}
        richColors={!workerPhone}
        closeButton={!workerPhone}
        {...(workerPhone
          ? { mobileOffset: { bottom: PHONE_TOAST_BOTTOM_PX }, offset: { bottom: PHONE_TOAST_BOTTOM_PX } }
          : /* Under the app bar, never over its bell and Sign out (owner, 2026-10-26). */
            { offset: { top: 'calc(var(--header-height) + var(--gap-component))' } })}
        {...(workerPhone ? { toastOptions: { classNames: PHONE_TOAST } } : {})}
      />
    </div>
    </div>
  );
}
